import {Component, OnInit} from '@angular/core';
import {FormArray, FormControl, FormGroup, Validators} from "@angular/forms";
import {ActivatedRoute} from "@angular/router";
import {
  Customer,
  defaultExtraColumnsSettings,
  DynamicField,
  ExtraColumnsSettings,
  StatisticDimension,
  Translation
} from "../../../../core/models";
import {CoApplicantsService, CustomersService, DynamicFieldsService, StatisticsService, TranslationService} from "../../../../core/services";

@Component({
    selector: 'app-extra-columns-settings',
    templateUrl: './extra-columns-settings.component.html',
    styleUrls: ['./extra-columns-settings.component.scss'],
    standalone: false
})
export class ExtraColumnsSettingsComponent implements OnInit {
  language: string;
  settings: ExtraColumnsSettings | undefined;
  statisticDimensions: StatisticDimension[];
  dynamicFields: DynamicField[];
  coApplicantGroups: string[];

  customer: Customer;

  value: ExtraColumnsSettings = {
    ...defaultExtraColumnsSettings
  };
  form: FormGroup = new FormGroup({
    fields: new FormArray([])
  });

  loading: boolean = false;
  selectedRefreshColumn: string = null;

  constructor(private route: ActivatedRoute,
              private customersService: CustomersService,
              private statisticService: StatisticsService,
              private dynamicFieldsService: DynamicFieldsService,
              private coApplicantsService: CoApplicantsService,
              private translationService: TranslationService) {
    this.loading = true;
    this.route.data.subscribe(data => {
      this.loading = false;
      this.customer = data['customer'];
      this.value = {
        ...defaultExtraColumnsSettings,
        ...this.customer.settings.extra_columns
      };
      this.value.fields.forEach(_ => this.doAddField());
      this.form.patchValue(this.value);
      this.form.disable();
      this.language = this.customer.settings.board?.languages?.[0] || this.customer.settings.board?.language || 'da';
    });
  }

  ngOnInit(): void {
    this.form.disable();
    this.loadStatisticDimensions();
    this.loadDynamicFields();
    this.loadCoApplicantGroups();
  }

  loadStatisticDimensions(): void {
    this.statisticService.getAllStatisticDimensions(this.customer.subdomain)
      .subscribe(dimensions => {
        this.statisticDimensions = dimensions
      });
  }

  loadDynamicFields(): void {
    this.dynamicFieldsService.getAll(this.customer.subdomain)
      .subscribe(dynamicFields => this.dynamicFields = dynamicFields);
  }

  loadCoApplicantGroups(): void {
    this.coApplicantsService.getAll(this.customer.subdomain)
      .subscribe(groups => this.coApplicantGroups = groups);
  }

  selectRefreshColumn(column_type: string): void {
    this.selectedRefreshColumn = column_type;
  }

  refreshColumn(): void {
    if (!this.selectedRefreshColumn) {
      return
    }
    this.customersService.refreshExtraColumns(this.customer, this.selectedRefreshColumn).subscribe(() => {
      this.selectedRefreshColumn = null;
    });
  }

  doEdit(): void {
    this.form.enable();
  }

  doCancel(): void {
    this.form.disable();
  }

  doSave(): void {
    this.loading = true;
    this.saveTranslations();
    const fields: {key: string, size: string}[] = [];
    this.fields.controls.forEach((control, i) => {
      fields.push({
        key: this.getKey(i).value,
        size: this.getSize(i).value
      })
    })
    console.log(fields);
    this.customer.settings.extra_columns = { ...this.customer.settings.extra_columns, ...this.form.value };
    this.customersService.update(this.customer)
      .subscribe(customer => {
        this.loading = false;
        this.customer = customer;
        this.value = this.form.value;
        this.form.disable();
      })
  }

  saveTranslations(): void {
    const translations: Translation[] = [];
    this.fields.controls.forEach((control, i) => {
      translations.push({
        key: 'grant-board.edit-display-dialog.content.field.' + this.getKey(i).value,
        value: this.getTranslation(i).value
      });
      translations.push({
        key: 'grant-board.applications-list.field.' + this.getKey(i).value,
        value: this.getTranslation(i).value
      });
    })
    this.translationService.addNewTranslations(this.customer.subdomain, this.customer.settings.board?.languages?.[0] || this.customer.settings.board?.language || 'da', translations)
      .subscribe((value: any) => {})
  }


  doAddField(): void {
    this.fields.push(new FormGroup({
      key: new FormControl('', Validators.required),
      size: new FormControl('', Validators.required),
      translation: new FormControl('', Validators.required)
    }));
  }

  doRemoveField(index: number): void {
    this.fields.removeAt(index);
  }

  get fields(): FormArray {
    return this.form.get('fields') as FormArray;
  }

  fieldAt(index: number): FormControl {
    return this.fields.at(index) as FormControl;
  }

  getKey(index: number): FormControl {
    return this.fieldAt(index)?.get('key') as FormControl;
  }

  getSize(index: number): FormControl {
    return this.fieldAt(index)?.get('size') as FormControl;
  }

  getTranslation(index: number): FormControl {
    return this.fieldAt(index).get('translation') as FormControl;
  }

  getDynamicField(index: number): string | undefined {
    if (!this.dynamicFields || this.dynamicFields.length === 0) {
      return '--';
    }
    const id = this.getKey(index).value.split('extra_columns.dynamic_field.')[1];
    const field = this.dynamicFields.find(dynamicField => dynamicField.id.toString() === id);
    return field?.title;
  }

  getStatistic(index: number): string | undefined {
    if (!this.statisticDimensions || this.statisticDimensions.length === 0) {
      return '--'
    }
    const uuid = this.getKey(index).value.split('extra_columns.statistic.')[1];

    const dimension = this.statisticDimensions.find(dimension => dimension.uuid === uuid);
    return dimension?.title;
  }

  getCoApplicantGroup(index: number): string | undefined {
    // Format is 'extra_columns.co_applicant.<group>.<company|name>'
    const value = this.getKey(index).value.split('.');
    return `${value[2]} (${value[3]})`;
  }
}
